import type { OctopusTextTheme, OctopusTheme } from "@octopusdeploy/design-system-tokens";
import { fontFamilyGlobal, textTheme } from "@octopusdeploy/design-system-tokens";
import { useLayoutEffect } from "react";
import type { ThemeName } from "./Theme";
import { getTheme } from "./utils/getTheme";
export function useApplyGlobalThemeEffect(themeName: ThemeName, customFontOverride: string | undefined, updateFontFamilyOnBody: boolean) {
    useLayoutEffect(() => {
        const theme: OctopusTheme = getTheme(themeName);
        // This can be simplified once the feature toggle for switching between fonts is removed
        const updatedTextTheme: OctopusTextTheme = {
            ...textTheme,
            ...(customFontOverride && { fontFamilyDefault: customFontOverride }),
        };
        // We want to manage the body separately so we can see where the cascade is being used
        const fontFamilyOnBody = updateFontFamilyOnBody && customFontOverride ? updatedTextTheme.fontFamilyDefault : fontFamilyGlobal["system-ui"];
        document.documentElement.style.setProperty("--fontFamilyBody", fontFamilyOnBody);
        const themeCssVariableProps = Object.entries(theme)
            .concat(Object.entries(updatedTextTheme))
            .map(([cssVariableName, value]) => `--${cssVariableName}: ${value};`);
        const serializedCss = `:root {
${themeCssVariableProps.join("\n")}
}`;
        // We manually inject the styles ourselves rather than using injectGlobals from emotion
        // injectGlobals does not have a way to remove or replace previously injected styles, so changing themes causes a new global style to be added
        let styleElement = document.querySelector(`#${globalStyleNodeId}`);
        if (styleElement) {
            styleElement.innerHTML = serializedCss;
        }
        else {
            styleElement = document.createElement("style");
            styleElement.setAttribute("id", globalStyleNodeId);
            styleElement.innerHTML = serializedCss;
            document.head.appendChild(styleElement);
        }
    }, [themeName, customFontOverride, updateFontFamilyOnBody]);
}
const globalStyleNodeId = "octopus-global-theme";
